import Layout from '@comp/layout'
import SolutionMeta from '@comp/solution/solution-meta'
import SolutionHero from '@comp/solution/solution-hero'
import SolutionPillars, { Pillar } from '@comp/solution/solution-pillars'
import ProductHowItWorks, { Step } from '@comp/product/product-how-it-works'
import SolutionRelated, { RelatedItem } from '@comp/solution/solution-related'
import FinalCta from '@comp/home/final-cta'

const pillars: Pillar[] = [
  {
    title: 'Video + keystroke, indexed',
    description:
      'Every privileged session is recorded as both a video stream and a structured keystroke log. Search the log, jump to the video timestamp instantly.',
    bullets: [
      'Full video capture of RDP, SSH, web sessions',
      'Keystroke and command-line transcription',
      'Searchable transcript with timestamp jump',
      'Configurable retention by sensitivity tier',
    ],
  },
  {
    title: 'Continuous monitoring, not just recording',
    description:
      'Detect risky commands and credential dumping in real time. Trigger alerts or terminate sessions on policy violations.',
    bullets: [
      'Live session view for security operators',
      'Pattern alerts on dangerous commands',
      'Kill-session controls for high-risk events',
      'Anomaly detection on session duration and access pattern',
    ],
  },
  {
    title: 'Evidence your auditors can actually use',
    description:
      'Recordings are tamper-evident, exportable, and chain-of-custody preserved. Built for SOC 2, ISO 27001, PCI-DSS, and forensic investigations.',
    bullets: [
      'Tamper-evident storage with hash verification',
      'Export to MP4 + structured log bundle',
      'Chain-of-custody metadata preserved',
      'Searchable across millions of sessions',
    ],
  },
  {
    title: 'Privacy by design',
    description:
      'Mask sensitive fields, redact secrets in keystroke logs, and apply least-privilege access to the recordings themselves.',
    bullets: [
      'Password field auto-redaction',
      'Configurable PII masking rules',
      'Recording access requires its own approval',
      'GDPR / KVKK-ready retention policies',
    ],
  },
]

const steps: Step[] = [
  {
    title: 'Route through the gateway',
    description:
      'Privileged sessions go through the Monopam gateway: RDP, SSH, web, or database. No agent on the target host required.',
  },
  {
    title: 'Record and index',
    description:
      'Video and keystrokes are captured, transcribed, and indexed. Storage is encrypted and tamper-evident.',
  },
  {
    title: 'Review, alert, export',
    description:
      'Search by user, command, or asset. Get alerts on risky behavior. Export evidence packets in one click.',
  },
]

const related: RelatedItem[] = [
  {
    label: 'Monopam Gateway',
    description:
      'Agentless gateway for RDP, SSH, web, and database access.',
    href: '/pam/gateway',
  },
  {
    label: 'Privileged vault',
    description:
      'Credential vault with rotation, check-out, and brokered access.',
    href: '/pam/vault',
  },
  {
    label: 'Just-in-time access',
    description:
      'Time-bound, approval-driven privileged access.',
    href: '/pam/jit-access',
  },
]

export default function SessionRecordingPage() {
  return (
    <Layout>
      <SolutionMeta
        title="Privileged session recording"
        description="Tamper-evident video and keystroke recording for every privileged session. Searchable transcripts, real-time alerts, audit-ready exports."
        path="/pam/session-recording"
      />
      <main className="bg-surface text-ink-primary">
        <SolutionHero
          eyebrow="Monopam · Session recording"
          title={
            <>
              Every privileged session,
              <br />
              on the record.
            </>
          }
          body="Video plus keystrokes, indexed and searchable, for every RDP, SSH, web, and database session. Tamper-evident storage. Real-time alerts. Evidence your auditors can hold."
          badges={[
            'Video + keystroke capture',
            'Real-time alerts',
            'Tamper-evident storage',
          ]}
        />
        <SolutionPillars
          title={<>Recording is table stakes. Searchable is the upgrade.</>}
          body="Most PAM tools record sessions. Monopam makes them useful by transcribing every keystroke, indexing every command, and giving you alerts when something looks wrong while it is still happening."
          pillars={pillars}
        />
        <ProductHowItWorks
          title={<>Three steps to defensible privileged access.</>}
          steps={steps}
        />
        <SolutionRelated items={related} />
        <FinalCta />
      </main>
    </Layout>
  )
}
